import { useTranslation } from 'react-i18next';
import { ArrowDownTrayIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { CellContext } from '@tanstack/react-table';

import { useWebEDIStore } from 'stores/webedi/webedi';
import { TransactionState } from 'support/types';
import { WebEDIRowData } from 'support/types/webediTable';
import { Button } from 'components/Form/Button/Button';
import { LoadingSpinner } from 'components/Loading/LoadingSpinner';

import { useSsccLabels } from '../hooks/useSsccLabels';

type SsccLabelActionsProps<TData, TValue> = {
  cell?: CellContext<TData, TValue>['cell'];
};

export const SsccLabelActions = <TData extends WebEDIRowData, TValue>({
  cell,
}: SsccLabelActionsProps<TData, TValue>) => {
  const { t } = useTranslation();
  const { print, download, isPrinting, isDownloading } = useSsccLabels();
  const isMessageInProgress = useWebEDIStore((state) => state.messageState === TransactionState.pending);
  const sscc = cell?.row.original.unit_identifier as string | undefined;
  const pdfUrl = (cell?.row.original.transport_label as { pdf_url?: string } | undefined)?.pdf_url;

  if (!sscc) {
    return null;
  }

  if (!pdfUrl) {
    return isMessageInProgress ? <LoadingSpinner sizeClassName="size-5" /> : null;
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="secondary"
        size="extra-small"
        LeftIcon={PrinterIcon}
        loading={isPrinting}
        disabled={isPrinting}
        onClick={() => print([pdfUrl])}
        analyticsId="webedi:transport_units_print_sscc_label"
      >
        {t('webedi:transport_units.sscc_label.print_button.text')}
      </Button>
      <Button
        variant="secondary"
        size="extra-small"
        LeftIcon={ArrowDownTrayIcon}
        loading={isDownloading}
        disabled={isDownloading}
        onClick={() => download([pdfUrl])}
        analyticsId="webedi:transport_units_download_sscc_label"
      >
        {t('webedi:transport_units.sscc_label.download_button.text')}
      </Button>
    </div>
  );
};
